import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Autocomplete,
  Checkbox,
  Switch,
  Box,
  Typography,
  Alert,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Description as DocIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountBalance as BankIcon
} from '@mui/icons-material';
import { SERVICE_CATEGORIES } from '../../services/internal-service.service';
import { FormHelperText } from '@mui/material';
import ApiService from '../../services/api.service';
import { can } from '../../utils/roleUtils';
import tcDocumentTypeService from '../../services/tc-document-type.service';
import serviceFieldService, { FIELD_TYPES } from '../../services/service-field.service';
import { PAYABLE_TYPES, accountTypeOf, accountTitle } from '../../components/accounting/PaymentSourceFields';
import { unwrapList } from '../../utils/apiConvert';

const TAX_RATE = 0.05; // 5% VAT

// Same text scale as the other service catalog dialogs (inputs/labels 13px, helper text and buttons 12px).
const f = { xs: '0.75rem', sm: '0.8125rem' };
const FORM_SX = {
  '& .MuiInputBase-root': { fontSize: f.sm },
  '& .MuiInputLabel-root': { fontSize: f.sm },
  '& .MuiFormHelperText-root': { fontSize: f.xs },
};
const MENU_PROPS = { PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: f.sm, minHeight: 32 } } } };

// Find one of the TC's categories by name (case-insensitive).
const findCategoryByName = (categories, name) => {
  if (!name) return null;
  const n = String(name).trim().toLowerCase();
  return categories.find(c => (c.name || '').toLowerCase() === n) || null;
};

const InternalServiceDialog = ({ open, onClose, onSave, service, categories = [], hideCreatesTask = false }) => {
  const isEdit = Boolean(service);
  // Category the service had when the dialog opened; an unchanged category isn't re-sent on save,
  // so editing a not-yet-backfilled service never wipes its old category.
  const [initialCategoryId, setInitialCategoryId] = useState('');
  // An old category string with no matching TC category (shown as a hint under the picker).
  const [legacyCategory, setLegacyCategory] = useState(null);
  const [initialBankAccountId, setInitialBankAccountId] = useState('');
  const [accounts, setAccounts] = useState([]);

  // Default for a new service: the TC's "Other" category if it has one, else none.
  const defaultCategoryId = () => findCategoryByName(categories, 'Other')?.id || '';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    serviceChargeIncl: '',   // user-typed field: incl. tax
    serviceCharge: '',       // excl. tax (computed, sent to API)
    taxOnServiceCharge: '',  // tax portion (computed, sent to API)
    govtCharge: '',
    bankAccountId: '',
    createsTask: false,
    uniformPricing: true,
    status: 'ACTIVE'
  });

  const [errors, setErrors] = useState({});

  // "Copy from Thaklis" — pull the name/description/category/charges from an existing Thaklis service
  // into this internal-service form (available for all TCs; the copied service is a plain internal one).
  const [copyOpen, setCopyOpen] = useState(false);
  const [thaklisServices, setThaklisServices] = useState([]);
  const [loadingThaklis, setLoadingThaklis] = useState(false);
  const [copySearch, setCopySearch] = useState('');

  // Document requirements (edit mode only)
  const [docTypes, setDocTypes] = useState([]);
  const [docReqs, setDocReqs] = useState([]); // [{tcDocumentTypeId, required, notes}]
  const [docReqsLoaded, setDocReqsLoaded] = useState(false);

  // Custom fields
  const [customFields, setCustomFields] = useState([]); // [{code, label, fieldType, required, options, placeholder, tcCustomFieldId?}]
  const [customFieldsLoaded, setCustomFieldsLoaded] = useState(false);
  // The TC's reusable custom-field catalog, for the "add existing field" picker.
  const [fieldCatalog, setFieldCatalog] = useState([]);

  const openCopyDialog = async () => {
    setCopyOpen(true);
    setCopySearch('');
    if (thaklisServices.length === 0) {
      setLoadingThaklis(true);
      try {
        const resp = await ApiService.getServices();
        setThaklisServices(resp?.data?.data || []);
      } catch (e) {
        setThaklisServices([]);
      } finally {
        setLoadingThaklis(false);
      }
    }
  };

  // Seed the form from a source service (Thaklis or provider). Both shapes carry camelCase and/or
  // snake_case fields, so read both. Copying a provider's service verbatim (esp. its name) is what
  // lets WO delegation later auto-match this internal service to the provider's by name.
  const seedFromService = (svc) => {
    const excl = Number(svc.serviceCharge ?? svc.service_charge ?? 0) || 0;
    const tax = Number(svc.taxOnServiceCharge ?? svc.tax_on_service_charge ?? (excl * TAX_RATE)) || 0;
    const incl = excl + tax;
    const govt = Number(svc.govtCharge ?? svc.govt_charge ?? 0) || 0;
    // Match the source's category to one of the TC's categories by name (or by the old label of a
    // code such as VISA → "Visa Services"); otherwise fall back to "Other", else leave it unset.
    const legacyLabel = SERVICE_CATEGORIES.find(c => c.value === svc.category)?.label;
    const cat = findCategoryByName(categories, svc.category)
      || findCategoryByName(categories, legacyLabel)
      || findCategoryByName(categories, 'Other');
    setFormData(prev => ({
      ...prev,
      name: svc.name || svc.service_name || prev.name,
      description: svc.description || prev.description,
      categoryId: cat?.id || '',
      serviceChargeIncl: incl > 0 ? incl.toFixed(2) : '',
      serviceCharge: excl > 0 ? excl.toFixed(2) : '',
      taxOnServiceCharge: tax > 0 ? tax.toFixed(2) : '',
      govtCharge: govt > 0 ? govt.toString() : ''
    }));
    setErrors({});
  };

  const applyThaklisService = (svc) => { seedFromService(svc); setCopyOpen(false); };

  const filteredThaklis = thaklisServices.filter(s => {
    const q = copySearch.trim().toLowerCase();
    if (!q) return true;
    const name = (s.name || s.service_name || '').toLowerCase();
    const code = (s.code || '').toLowerCase();
    return name.includes(q) || code.includes(q);
  });

  // "Copy from provider" — offered only when the TC has exactly ONE connected provider (add mode).
  // Pulls that provider's services priced for this client (the same list the WO-delegation dialog shows).
  const [provider, setProvider] = useState(null); // { connection_id, b2b_name, b2b_code } | null
  const [providerCopyOpen, setProviderCopyOpen] = useState(false);
  const [providerServices, setProviderServices] = useState([]);
  const [loadingProvider, setLoadingProvider] = useState(false);
  const [providerSearch, setProviderSearch] = useState('');

  const canReadWorkOrder = (() => {
    try { return can(JSON.parse(localStorage.getItem('currentUser') || '{}'), 'work_order', 'read'); } catch (e) { return false; }
  })();

  useEffect(() => {
    if (!open || isEdit || !canReadWorkOrder) { setProvider(null); return; }
    let active = true;
    (async () => {
      try {
        const resp = await ApiService.getB2bPartners();
        const partners = resp?.data?.data || [];
        if (active) setProvider(partners.length === 1 ? partners[0] : null);
      } catch (e) {
        if (active) setProvider(null);
      }
    })();
    return () => { active = false; };
  }, [open, isEdit, canReadWorkOrder]);

  const openProviderCopyDialog = async () => {
    setProviderCopyOpen(true);
    setProviderSearch('');
    if (provider && providerServices.length === 0) {
      setLoadingProvider(true);
      try {
        const resp = await ApiService.getB2bServices(provider.connection_id);
        setProviderServices(resp?.data?.data || []);
      } catch (e) {
        setProviderServices([]);
      } finally {
        setLoadingProvider(false);
      }
    }
  };

  const applyProviderService = (svc) => { seedFromService(svc); setProviderCopyOpen(false); };

  const filteredProvider = providerServices.filter(s => {
    const q = providerSearch.trim().toLowerCase();
    if (!q) return true;
    return (s.name || '').toLowerCase().includes(q);
  });

  // Recompute excl/tax when editing an existing service.
  // API returns snake_case (service_charge, tax_on_service_charge, etc.)
  const buildFromService = (svc) => {
    const excl = svc.service_charge ?? svc.serviceCharge ?? 0;
    const tax = svc.tax_on_service_charge ?? svc.taxOnServiceCharge ?? Math.round(excl * TAX_RATE * 100) / 100;
    const incl = excl + tax;
    return {
      name: svc.name || '',
      description: svc.description || '',
      categoryId: svc.category_id ?? svc.categoryId ?? findCategoryByName(categories, svc.category)?.id ?? '',
      serviceChargeIncl: incl > 0 ? incl.toFixed(2) : '',
      serviceCharge: excl > 0 ? excl.toFixed(2) : '',
      taxOnServiceCharge: tax > 0 ? tax.toFixed(2) : '',
      govtCharge: (svc.govt_charge ?? svc.govtCharge)?.toString() || '',
      bankAccountId: svc.bank_account_id ?? svc.bankAccountId ?? '',
      createsTask: svc.creates_task ?? svc.createsTask ?? false,
      // null (legacy) is treated as uniform.
      uniformPricing: (svc.uniform_pricing ?? svc.uniformPricing) !== false,
      status: svc.status || 'ACTIVE'
    };
  };

  const emptyForm = () => ({
    name: '',
    description: '',
    categoryId: defaultCategoryId(),
    serviceChargeIncl: '',
    serviceCharge: '',
    taxOnServiceCharge: '',
    govtCharge: '',
    bankAccountId: '',
    createsTask: false,
    uniformPricing: true,
    status: 'ACTIVE'
  });

  useEffect(() => {
    if (open) {
      setFormData(service ? buildFromService(service) : emptyForm());
      setInitialBankAccountId(service ? (service.bank_account_id ?? service.bankAccountId ?? '') : '');
      ApiService.getTcBankAccounts().then(unwrapList).then((r) => setAccounts(r.items)).catch(() => setAccounts([]));
      // The stored link, not a name match: a service matched only by name gets linked on save.
      const storedCategoryId = service ? (service.category_id ?? service.categoryId ?? '') : '';
      setInitialCategoryId(storedCategoryId);
      setLegacyCategory(service && !storedCategoryId && service.category
        && !findCategoryByName(categories, service.category) ? service.category : null);
      setErrors({});
      setDocReqsLoaded(false);
      setCustomFieldsLoaded(false);
      // Load doc types + existing requirements
      tcDocumentTypeService.getActive().then(setDocTypes).catch(() => setDocTypes([]));
      // Load the TC custom-field catalog for the "add existing field" picker.
      serviceFieldService.getCatalog().then(setFieldCatalog).catch(() => setFieldCatalog([]));
      if (service?.id) {
        tcDocumentTypeService.getRequirements(service.id).then(reqs => {
          setDocReqs(reqs.map(r => ({ tcDocumentTypeId: r.tcDocumentTypeId, required: r.required !== false, notes: r.notes || '' })));
          setDocReqsLoaded(true);
        }).catch(() => { setDocReqs([]); setDocReqsLoaded(true); });
        // Load custom fields
        serviceFieldService.getFields(service.id).then(fields => {
          setCustomFields(fields.map(f => ({
            id: f.fieldId || f.id,
            tcCustomFieldId: f.tcCustomFieldId || f.fieldId,
            code: f.code || '',
            label: f.label || '',
            fieldType: f.field_type || f.fieldType || 'TEXT',
            required: f.required || false,
            options: f.options || [],
            placeholder: f.placeholder || ''
          })));
          setCustomFieldsLoaded(true);
        }).catch(() => { setCustomFields([]); setCustomFieldsLoaded(true); });
      } else {
        setDocReqs([]);
        setDocReqsLoaded(true);
        setCustomFields([]);
        setCustomFieldsLoaded(true);
      }
    }
  }, [open, service]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // When the incl. tax field changes, recompute excl and tax
  const handleServiceChargeInclChange = (event) => {
    const value = event.target.value;
    setFormData(prev => {
      const inclValue = parseFloat(value);
      if (!Number.isFinite(inclValue) || inclValue <= 0) {
        return {
          ...prev,
          serviceChargeIncl: value,
          serviceCharge: '',
          taxOnServiceCharge: ''
        };
      }
      const exclRounded = Math.round((inclValue / 1.05) * 100) / 100;
      const taxRounded = Math.round((inclValue - exclRounded) * 100) / 100;
      return {
        ...prev,
        serviceChargeIncl: value,
        serviceCharge: exclRounded.toFixed(2),
        taxOnServiceCharge: taxRounded.toFixed(2)
      };
    });
    if (errors.serviceChargeIncl) {
      setErrors(prev => ({ ...prev, serviceChargeIncl: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    }

    if (!formData.serviceChargeIncl) {
      newErrors.serviceChargeIncl = 'Service charge is required';
    } else if (isNaN(parseFloat(formData.serviceChargeIncl)) || parseFloat(formData.serviceChargeIncl) < 0) {
      newErrors.serviceChargeIncl = 'Enter a valid amount';
    }

    if (formData.govtCharge && (isNaN(parseFloat(formData.govtCharge)) || parseFloat(formData.govtCharge) < 0)) {
      newErrors.govtCharge = 'Enter a valid amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const serviceData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      // Always on create; on edit only when changed (see initialCategoryId).
      ...((!isEdit || formData.categoryId !== initialCategoryId) && { categoryId: formData.categoryId || null }),
      serviceCharge: parseFloat(formData.serviceCharge) || 0,
      taxOnServiceCharge: parseFloat(formData.taxOnServiceCharge) || 0,
      govtCharge: parseFloat(formData.govtCharge) || 0,
      // Sent on create, and on edit only when changed, so a failed account load never clears it.
      ...((!isEdit || formData.bankAccountId !== initialBankAccountId) && { bankAccountId: formData.bankAccountId || null }),
      createsTask: formData.createsTask,
      uniformPricing: formData.uniformPricing,
      status: formData.status
    };

    // Pass docReqs along so parent can save them after creating/updating the service
    serviceData._docRequirements = docReqs.filter(r => r.tcDocumentTypeId);
    // Pass custom fields along
    serviceData._customFields = customFields.filter(f => f.code && f.label)
      .map(({ optionsText, ...field }) => field); // optionsText is UI-only
    onSave(serviceData);
  };

  const toggleDocReq = (dtId) => {
    setDocReqs(prev => {
      const exists = prev.find(r => r.tcDocumentTypeId === dtId);
      if (exists) return prev.filter(r => r.tcDocumentTypeId !== dtId);
      return [...prev, { tcDocumentTypeId: dtId, required: true, notes: '' }];
    });
  };

  const updateDocReq = (dtId, field, value) => {
    setDocReqs(prev => prev.map(r =>
      r.tcDocumentTypeId === dtId ? { ...r, [field]: value } : r
    ));
  };

  const selectedDocIds = new Set(docReqs.map(r => r.tcDocumentTypeId));
  const payableAccounts = accounts.filter(a => a.isActive !== false && PAYABLE_TYPES.includes(accountTypeOf(a)));

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: f.sm }}>
        {isEdit ? 'Edit Internal Service' : 'Add Internal Service'}
      </DialogTitle>
      <DialogContent sx={FORM_SX}>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={1.5}>
            {/* Copy-from (add mode only) — seeds the form from a Thaklis service, or from the sole
                connected provider's service so its name matches for later WO delegation. */}
            {!isEdit && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" startIcon={<CopyIcon />} onClick={openCopyDialog} sx={{ fontSize: f.xs }}>
                    Copy from Thaklis service
                  </Button>
                  {provider && (
                    <Button variant="outlined" size="small" startIcon={<CopyIcon />} onClick={openProviderCopyDialog} sx={{ fontSize: f.xs }}>
                      Copy from provider
                    </Button>
                  )}
                </Box>
              </Grid>
            )}
            {/* Service Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Service Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={Boolean(errors.name)}
                helperText={errors.name}
                required
                placeholder="e.g., Translation Service"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={2}
                placeholder="Brief description of the service"
              />
            </Grid>

            {/* Category and Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.categoryId}
                  label="Category"
                  MenuProps={MENU_PROPS}
                  onChange={handleChange('categoryId')}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
                {legacyCategory && !formData.categoryId && (
                  <FormHelperText>Currently "{legacyCategory}" (not linked to a category)</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  MenuProps={MENU_PROPS}
                  onChange={handleChange('status')}
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Service Charge (Incl. Tax) — primary input */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Service Charge (Incl. Tax)"
                type="number"
                value={formData.serviceChargeIncl}
                onChange={handleServiceChargeInclChange}
                error={Boolean(errors.serviceChargeIncl)}
                helperText={errors.serviceChargeIncl || '5% VAT included'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">AED</InputAdornment>
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Government Charge */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Government Charge"
                type="number"
                value={formData.govtCharge}
                onChange={handleChange('govtCharge')}
                error={Boolean(errors.govtCharge)}
                helperText={errors.govtCharge || 'Optional - for pass-through fees'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">AED</InputAdornment>
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Read-only breakdown: excl. tax and tax */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Service Charge (Excl. Tax)"
                type="number"
                value={formData.serviceCharge}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">AED</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Tax (5% VAT)"
                type="number"
                value={formData.taxOnServiceCharge}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">AED</InputAdornment>
                }}
              />
            </Grid>

            {/* Pricing model: uniform (same for all) vs per-organization mapping */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.uniformPricing}
                    onChange={(e) => setFormData(prev => ({ ...prev, uniformPricing: e.target.checked }))}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontSize: f.sm }}>Same price for all clients</Typography>}
              />
              <Alert severity={formData.uniformPricing ? 'success' : 'warning'}
                sx={{ mt: 0.5, py: 0, '& .MuiTypography-body2': { fontSize: f.xs } }}>
                {formData.uniformPricing ? (
                  <Typography variant="body2">
                    This service will use the <strong>same price for all clients</strong> — no per-client
                    price mapping is needed.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Price must be <strong>mapped for each client</strong> (Service Catalog → Create Mapping).
                    Until a client is mapped, this service <strong>won't appear</strong> when adding it to a
                    work order or invoice for that client.
                  </Typography>
                )}
              </Alert>
            </Grid>

            {/* Creates Task Checkbox — hidden for connections-enabled TCs (they delegate to B2Bs, never auto-task) */}
            {!hideCreatesTask && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  backgroundColor: formData.createsTask ? 'rgba(87, 148, 242, 0.04)' : 'inherit',
                  borderColor: formData.createsTask ? 'primary.main' : 'divider'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.createsTask}
                      onChange={handleChange('createsTask')}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <TaskIcon color={formData.createsTask ? 'primary' : 'action'} />
                      <Typography fontWeight={500} sx={{ fontSize: f.sm }}>
                        Creates Internal Task
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ ml: 4, mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: f.xs }}>
                    When enabled, adding this service to a work order will create a task
                    that can be assigned to typing center employees for tracking and completion.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            )}

            {/* Required Documents */}
            {docTypes.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <DocIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: f.sm }}>
                    Required Documents ({docReqs.length} selected)
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Select which documents are needed for this service
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {docTypes.map(dt => {
                    const selected = selectedDocIds.has(dt.id);
                    const req = docReqs.find(r => r.tcDocumentTypeId === dt.id);
                    return (
                      <Box key={dt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Checkbox size="small" checked={selected} onChange={() => toggleDocReq(dt.id)} />
                        <Typography sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0 }} noWrap>
                          {dt.label}
                        </Typography>
                        {selected && (
                          <>
                            <FormControlLabel
                              control={<Switch size="small" checked={req?.required !== false} onChange={e => updateDocReq(dt.id, 'required', e.target.checked)} />}
                              label={<Typography sx={{ fontSize: '0.65rem' }}>{req?.required !== false ? 'Required' : 'Optional'}</Typography>}
                              sx={{ ml: 0, mr: 0 }}
                            />
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
            )}

            {/* Custom Fields */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TuneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: f.sm }}>
                      Custom Fields ({customFields.length})
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 220 }}
                      options={fieldCatalog.filter(cf =>
                        !customFields.some(f => (f.tcCustomFieldId || f.id) === cf.id || f.code === cf.code))}
                      getOptionLabel={(o) => o.label || o.code || ''}
                      value={null}
                      blurOnSelect
                      onChange={(e, cf) => {
                        if (!cf) return;
                        setCustomFields(prev => [...prev, {
                          id: cf.id,
                          tcCustomFieldId: cf.id,
                          code: cf.code,
                          label: cf.label,
                          fieldType: cf.fieldType || cf.field_type || 'TEXT',
                          required: false,
                          options: cf.options || [],
                          placeholder: cf.placeholder || ''
                        }]);
                      }}
                      renderInput={(params) => <TextField {...params} label="Add existing field" placeholder="Pick from catalog" />}
                    />
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setCustomFields(prev => [...prev, {
                        code: '', label: '', fieldType: 'TEXT', required: false,
                        options: [], placeholder: ''
                      }])}
                    >
                      Add new
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Define extra data fields collected when this service is added to a work order
                </Typography>
                {customFields.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                    No custom fields defined
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {customFields.map((field, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, backgroundColor: 'action.hover' }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth size="small" label="Label" value={field.label}
                              disabled={!!field.tcCustomFieldId}
                              onChange={(e) => {
                                const updated = [...customFields];
                                updated[idx] = { ...updated[idx], label: e.target.value,
                                  code: updated[idx].code || e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') };
                                setCustomFields(updated);
                              }}
                              placeholder="e.g., Medical Center"
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <TextField
                              fullWidth size="small" label="Code" value={field.code}
                              disabled={!!field.tcCustomFieldId}
                              onChange={(e) => {
                                const updated = [...customFields];
                                updated[idx] = { ...updated[idx], code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') };
                                setCustomFields(updated);
                              }}
                              placeholder="FIELD_CODE"
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={field.fieldType} label="Type"
                                disabled={!!field.tcCustomFieldId}
                                onChange={(e) => {
                                  const updated = [...customFields];
                                  updated[idx] = { ...updated[idx], fieldType: e.target.value };
                                  setCustomFields(updated);
                                }}
                              >
                                {FIELD_TYPES.map(ft => (
                                  <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <FormControlLabel
                              control={
                                <Switch size="small" checked={field.required}
                                  onChange={(e) => {
                                    const updated = [...customFields];
                                    updated[idx] = { ...updated[idx], required: e.target.checked };
                                    setCustomFields(updated);
                                  }}
                                />
                              }
                              label={<Typography sx={{ fontSize: '0.75rem' }}>Required</Typography>}
                            />
                          </Grid>
                          <Grid item xs={6} sm={2} sx={{ textAlign: 'right' }}>
                            <IconButton size="small" color="error"
                              onClick={() => setCustomFields(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          {field.fieldType === 'SELECT' && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth size="small"
                                label="Options (comma-separated)"
                                disabled={!!field.tcCustomFieldId}
                                // optionsText keeps what is typed (commas and spaces included); the options list is derived
                                value={field.optionsText ?? (field.options || []).map(o => typeof o === 'string' ? o : (o.value || o.label || '')).join(', ')}
                                onChange={(e) => {
                                  const updated = [...customFields];
                                  const opts = e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                                    .map(v => ({ value: v, label: v }));
                                  updated[idx] = { ...updated[idx], options: opts, optionsText: e.target.value };
                                  setCustomFields(updated);
                                }}
                                placeholder="Option 1, Option 2, Option 3"
                                helperText="Enter dropdown options separated by commas"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Account the service's government fee, fines and add-ons are normally paid from */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BankIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: f.sm }}>
                    Paid from
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={payableAccounts.some(a => a.id === formData.bankAccountId) ? formData.bankAccountId : ''}
                    MenuProps={MENU_PROPS}
                    onChange={handleChange('bankAccountId')}
                    sx={{ fontSize: f.sm }}
                  >
                    <MenuItem value=""><em>Default account</em></MenuItem>
                    {payableAccounts.map(a => (
                      <MenuItem key={a.id} value={a.id}>
                        {accountTitle(a)}{accountTypeOf(a) === 'CASH' ? ' (cash)' : accountTypeOf(a) === 'E_WALLET' ? ' (e-wallet)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Pre-selected when a government fee, fine or add-on for this service is paid. The default account is used if empty.
                </Typography>
              </Paper>
            </Grid>

            {/* Info Alert */}
            <Grid item xs={12}>
              <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0, '& .MuiTypography-body2': { fontSize: f.xs } }}>
                <Typography variant="body2">
                  <strong>Internal Services</strong> are specific to your typing center.
                  They do not create Thaklis tickets but can be used in work orders and invoices
                  for additional billing items.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary', fontSize: f.xs }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} sx={{ fontSize: f.xs }}>
          {isEdit ? 'Update Service' : 'Save Service'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Copy-from-Thaklis picker */}
    <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: f.sm }}>Copy from a Thaklis service</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search Thaklis services..."
          value={copySearch}
          onChange={(e) => setCopySearch(e.target.value)}
          sx={{ mt: 1, mb: 1, '& .MuiInputBase-root': { fontSize: f.sm } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {loadingThaklis ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : filteredThaklis.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center', fontSize: f.sm }}>No Thaklis services found.</Typography>
        ) : (
          <List sx={{ maxHeight: 360, overflow: 'auto' }}>
            {filteredThaklis.map((svc) => (
              <ListItemButton key={svc.id || svc.code} onClick={() => applyThaklisService(svc)}>
                <ListItemText
                  primary={svc.name || svc.service_name}
                  secondary={svc.code ? `${svc.code}` : null}
                  primaryTypographyProps={{ fontSize: f.sm }}
                  secondaryTypographyProps={{ fontSize: f.xs }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setCopyOpen(false)} sx={{ color: 'text.secondary', fontSize: f.xs }}>Cancel</Button>
      </DialogActions>
    </Dialog>

    {/* Copy-from-provider picker */}
    <Dialog open={providerCopyOpen} onClose={() => setProviderCopyOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: f.sm }}>
        Copy from {provider ? `${provider.b2b_name} (${provider.b2b_code})` : 'provider'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search provider services..."
          value={providerSearch}
          onChange={(e) => setProviderSearch(e.target.value)}
          sx={{ mt: 1, mb: 1, '& .MuiInputBase-root': { fontSize: f.sm } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {loadingProvider ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : filteredProvider.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center', fontSize: f.sm }}>No provider services found.</Typography>
        ) : (
          <List sx={{ maxHeight: 360, overflow: 'auto' }}>
            {filteredProvider.map((svc) => {
              const total = Number(svc.service_charge || 0) + Number(svc.govt_charge || 0) + Number(svc.tax_on_service_charge || 0);
              return (
                <ListItemButton key={svc.id} onClick={() => applyProviderService(svc)}>
                  <ListItemText
                    primary={svc.name}
                    secondary={`${total.toFixed(2)} AED`}
                    primaryTypographyProps={{ fontSize: f.sm }}
                    secondaryTypographyProps={{ fontSize: f.xs }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setProviderCopyOpen(false)} sx={{ color: 'text.secondary', fontSize: f.xs }}>Cancel</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default InternalServiceDialog;
